<template>
	<ChalkyUtilityModal
		class="view-modal-global-checkin default-modal"
		v-bind:class="stateClass"
		v-model="isVisible"
		v-on:hide="Handle_OnHide"
	>
		<ChalkyUtilitySlideshow
			ref="slideshowView"
			v-bind:showDots="false"
			v-bind:showNavigation="false"
			v-bind:slideCount="3"
			v-on:change="Handle_OnSlideChange"
		>
			<template #slide-0>
				<header class="stepped-overlay-header">
					<h3>Checkin</h3>

					<p>
						If you&rsquo;re out shooting, let
						<br />
						people know where you're playing.
					</p>
				</header>

				<section class="primary-container stepped-overlay-content">
					<!-- <section >
						<figure>
							<picture>
								<img v-bind:src="nearbyVenueImageUrl" alt="Nearby Venue" class="rounded" />
							</picture>
						</figure>

						<div class="padded text-center push-top">
							<h4>Are you playing at...</h4>
							<h2>
								<strong>{{ nearbyVenueModel?.getName() }}</strong>
							</h2>
						</div>
					</section> -->

					<section
						class="nearby-venue-container"
						v-if="nearbyVenueModel && isActuallyNearby && useNearbySuggestion"
					>
						<ChalkyVenueMap
							class="hide-streetview rounded"
							v-bind:centerLatitude="mapLatitude"
							v-bind:centerLongitude="mapLongitude"
							v-bind:centerOffsetAlways="true"
							v-bind:centerOffsetY="centerOffsetY"
							v-bind:centerOnMarker="true"
							v-bind:userLatitude="$store.getters['location/latitude']"
							v-bind:userLongitude="$store.getters['location/longitude']"
							v-bind:venueCollection="nearbyVenueCollection"
							v-bind:zoom="mapZoom"
							v-on:marker:click="Handle_OnMarkerClick"
						/>

						<ChalkyVenueCardPreview class="on-light shadow" v-bind:venueModel="nearbyVenueModel" />
					</section>

					<section v-else>
						<!-- Venue tagging -->
						<label>
							<h6>Where are you?</h6>

							<!-- Selected venue display -->
							<ChalkyVenueRow v-if="selectedVenueModel" v-bind:venueModel="selectedVenueModel">
								<template v-slot:actions>
									<button
										class="control round size-medium theme-dark type-close"
										v-on:click="Handle_OnClickClearVenue"
									>
										Clear
									</button>
								</template>
							</ChalkyVenueRow>

							<!-- Venue search -->
							<ChalkyFormSearch
								class="size-small"
								placeholder="Search for a place"
								v-bind:autocomplete="true"
								v-bind:autocompleteDelay="300"
								v-bind:type="'venue'"
								v-bind:venueCollection="venueCollection"
								v-else
								v-on:autocomplete:select="Handle_OnSelectVenue"
							/>
						</label>
					</section>
				</section>

				<section class="action-container stepped-overlay-actions">
					<div class="button-group stacked">
						<button
							class="button-secondary size-medium"
							v-if="nearbyVenueModel && isActuallyNearby && useNearbySuggestion"
							v-on:click="Handle_OnClickSearch"
						>
							No, let me search...
						</button>

						<button
							class="button-primary size-medium"
							v-bind:disabled="!useNearbySuggestion && !selectedVenueModel"
							v-on:click="Handle_OnClickConfirmNearby"
						>
							Continue
						</button>
					</div>
				</section>
			</template>

			<template #slide-1>
				<section class="detector-container rounded" v-if="currentStep == 1">
					<ChalkyInputTableDetector
						v-bind:showOverlay="false"
						v-on:table:detected="Handle_OnTableDetected"
						v-on:table:progress="Handle_OnTableProgress"
					/>
				</section>

				<section class="venue-display" v-if="selectedVenueModel">
					<ChalkyVenueRow v-bind:venueModel="selectedVenueModel" />
				</section>
			</template>

			<template #slide-2>
				<section class="primary-container stepped-overlay-content">
					<section class="complete-container stepped-overlay-complete text-center">
						<figure class="push-bottom">
							<img
								class="icon filter-invert"
								src="~@chalkysticks/sass/build/asset/image/icon/checkmark.svg"
							/>
						</figure>

						<h3>You're checked in!</h3>

						<p>Now people know where the game's happening. Thanks for keeping the community in the loop.</p>
					</section>
				</section>

				<section class="action-container stepped-overlay-actions">
					<div class="button-group">
						<button class="button-secondary size-medium" v-on:click="Handle_OnClickClose">Close</button>
					</div>
				</section>
			</template>
		</ChalkyUtilitySlideshow>
	</ChalkyUtilityModal>
</template>

<script lang="ts">
	import * as Config from '@/Config';
	import ChalkySticks from '@chalkysticks/sdk';
	import Store from '@/Store';
	import ViewBase from '@/Core/View.vue';
	import { Component, Prop, Ref } from 'vue-property-decorator';
	import { beforeDestroy, mounted } from '@/Utility/Decorators';

	/**
	 * Global modal for checking into a venue.
	 * Two steps: Select Venue → Confirmation
	 *
	 * @author Matt Kenefick <matt@chalkysticks.com>
	 * @package View/Modal/Global
	 * @project ChalkySticks App
	 */
	@Component
	export default class ViewModalGlobalCheckin extends ViewBase {
		/**
		 * CSS state class based on the current step of the checkin flow.
		 *
		 * @return string
		 */
		public get stateClass(): string {
			if (this.hasCompleted) {
				return 'state-complete';
			}

			switch (this.currentStep) {
				case 0:
					return 'state-selection';
				case 1:
					return 'state-detection';
				case 2:
					return 'state-complete';
				default:
					return 'state-selection';
			}
		}

		/**
		 * Vertical pixel offset applied when centering the map on the marker,
		 * used to lift the pin above the overlaid venue card preview.
		 *
		 * @return number
		 */
		public get centerOffsetY(): number {
			return -85;
		}

		/**
		 * If we're actually nearby this place. Always true when the user has
		 * manually picked a marker, since they're explicitly choosing that venue.
		 *
		 * @type boolean
		 */
		public get isActuallyNearby(): boolean {
			// User explicitly tapped a marker -- respect their choice regardless of distance
			if (this.nearbyVenueOverride) {
				return true;
			}

			const latitude = Store.getters['location/latitude'] || Config.App.DEFAULT_LATITUDE;
			const longitude = Store.getters['location/longitude'] || Config.App.DEFAULT_LONGITUDE;
			const distance = this.nearbyVenueModel?.getDistance(latitude, longitude) || 9999;
			const threshold = 0.425; // 0.1 miles;

			ChalkySticks.Utility.Log.debug(
				'app:checkin',
				`Distance to nearby venue: ${distance} miles : `,
				this.nearbyVenueModel?.getName(),
			);

			return distance <= threshold;
		}

		/**
		 * Latitude used to center the embedded map, sourced from the
		 * nearby venue suggestion when available.
		 *
		 * @return number
		 */
		public get mapLatitude(): number {
			return this.nearbyVenueModel?.getLatitude() || 0;
		}

		/**
		 * Longitude used to center the embedded map, sourced from the
		 * nearby venue suggestion when available.
		 *
		 * @return number
		 */
		public get mapLongitude(): number {
			return this.nearbyVenueModel?.getLongitude() || 0;
		}

		/**
		 * Zoom level for the embedded venue map. Tight enough to show the
		 * single suggested venue clearly.
		 *
		 * @return number
		 */
		public get mapZoom(): number {
			return 15;
		}

		/**
		 * Get the venue model to preview -- the manually-selected marker if present,
		 * otherwise the nearest venue by distance from user's current location.
		 *
		 * @return ChalkySticks.Model.Venue | null
		 */
		public get nearbyVenueModel(): ChalkySticks.Model.Venue | null {
			// User explicitly tapped a marker -- use that venue
			if (this.nearbyVenueOverride) {
				return this.nearbyVenueOverride;
			}

			const latitude = Store.getters['location/latitude'] || Config.App.DEFAULT_LATITUDE;
			const longitude = Store.getters['location/longitude'] || Config.App.DEFAULT_LONGITUDE;

			const sorted = [...this.nearbyVenueCollection.models].sort((a, b) => {
				return a.getDistance(latitude, longitude, 'mi') - b.getDistance(latitude, longitude, 'mi');
			});

			return sorted[0] || null;
		}

		/**
		 * @return string
		 */
		protected get nearbyVenueImageUrl(): string {
			return this.nearbyVenueModel?.media.getSortedMedia()[0]?.getUrlLarge() || '';
		}

		/**
		 * @type ChalkySticks/Collection/Venue
		 */
		@Prop({
			default: () => ChalkySticks.Core.app('nearbyVenueCollection'),
		})
		public nearbyVenueCollection!: ChalkySticks.Collection.Venue;

		/**
		 * @type View
		 */
		@Ref('slideshowView')
		protected slideshowView!: any;

		/**
		 * Current step in the modal flow
		 *
		 * @type number
		 */
		protected currentStep: number = 0;

		/**
		 * Whether the checkin has completed successfully
		 *
		 * @type boolean
		 */
		protected hasCompleted: boolean = false;

		/**
		 * Whether a checkin request is in progress
		 *
		 * @type boolean
		 */
		protected isLoading: boolean = false;

		/**
		 * Whether the modal is visible
		 *
		 * @type boolean
		 */
		protected isVisible: boolean = false;

		/**
		 * Manually-selected venue via marker click. When set, overrides the
		 * distance-sorted nearest venue in the preview.
		 *
		 * @type ChalkySticks.Model.Venue | null
		 */
		protected nearbyVenueOverride: ChalkySticks.Model.Venue | null = null;

		/**
		 * The selected venue model
		 *
		 * @type ChalkySticks.Model.Venue | null
		 */
		protected selectedVenueModel: ChalkySticks.Model.Venue | null = null;

		/**
		 * Whether to show the nearby venue suggestion if available
		 *
		 * @type boolean
		 */
		protected useNearbySuggestion: boolean = true;

		/**
		 * Collection for venue search autocomplete
		 *
		 * @type ChalkySticks.Collection.Venue
		 */
		protected venueCollection: ChalkySticks.Collection.Venue = ChalkySticks.Factory.Venue.collection({
			limit: 8,
		});

		/**
		 * Attach event listeners on mount.
		 *
		 * @return void
		 */
		@mounted
		public attachEvents(): void {
			ChalkySticks.Core.Event.Bus.on('modal:checkin:open', this.Handle_OnOpen);
		}

		/**
		 * Detach event listeners before destroy.
		 *
		 * @return void
		 */
		@beforeDestroy
		public detachEvents(): void {
			ChalkySticks.Core.Event.Bus.off('modal:checkin:open', this.Handle_OnOpen);
		}

		/**
		 * Perform the checkin against the currently selected venue. Dispatches
		 * a success event on completion or shows an alert dialog on failure.
		 *
		 * @return Promise<void>
		 */
		protected async checkin(): Promise<void> {
			if (!this.selectedVenueModel || this.isLoading) {
				return;
			}

			this.isLoading = true;

			try {
				await this.selectedVenueModel.checkin();
				this.hasCompleted = true;

				// Dispatch success event
				ChalkySticks.Core.Event.Bus.dispatch('checkin:complete', {
					venueModel: this.selectedVenueModel,
				});
			} catch (error) {
				ChalkySticks.Utility.Log.error('checkin', 'Failed to check in:', error);

				// Show error dialog
				ChalkySticks.Core.Event.Bus.dispatch('dialog:alert', {
					message: 'Failed to check in. Please try again.',
					title: 'Error',
				});
			} finally {
				this.isLoading = false;
			}
		}

		/**
		 * @return void
		 */
		protected close(): void {
			this.isVisible = false;
			this.reset();
		}

		/**
		 * @return void
		 */
		protected reset(): void {
			this.currentStep = 0;
			this.hasCompleted = false;
			this.isLoading = false;
			this.nearbyVenueOverride = null;
			this.selectedVenueModel = null;
			this.useNearbySuggestion = true;

			this.slideshowView.goto(0);
		}

		/**
		 * Open the modal. If a venue model is provided, pre-populate it as the
		 * selected venue and jump straight to the scanner step (skipping the
		 * venue selection slide).
		 *
		 * @param ChalkySticks.Model.Venue | undefined venueModel
		 * @return void
		 */
		protected open(venueModel?: ChalkySticks.Model.Venue): void {
			this.reset();
			this.isVisible = true;

			// Pre-seeded venue -- skip selection and go directly to the scanner
			if (venueModel) {
				this.selectedVenueModel = venueModel;
				this.useNearbySuggestion = false;

				this.$nextTick(() => {
					this.slideshowView.goto(1);
				});
			}
		}

		// region: Event Handlers
		// ---------------------------------------------------------------------------

		/**
		 * Handle checkin button click.
		 *
		 * @param PointerEvent e
		 * @return Promise<void>
		 */
		protected async Handle_OnClickCheckin(e: PointerEvent): Promise<void> {
			e.preventDefault();

			await this.checkin();
		}

		/**
		 * Handle clear venue button click.
		 *
		 * @param PointerEvent e
		 * @return Promise<void>
		 */
		protected async Handle_OnClickClearVenue(e: PointerEvent): Promise<void> {
			e.preventDefault();

			this.selectedVenueModel = null;
		}

		/**
		 * Handle close button click.
		 *
		 * @param PointerEvent e
		 * @return Promise<void>
		 */
		protected async Handle_OnClickClose(e?: PointerEvent): Promise<void> {
			e?.preventDefault();

			this.isVisible = false;
			this.reset();
		}

		/**
		 * Handle continue button click.
		 *
		 * @param PointerEvent e
		 * @return Promise<void>
		 */
		protected async Handle_OnClickContinue(e: PointerEvent): Promise<void> {
			e.preventDefault();

			this.slideshowView.next();
		}

		/**
		 * @param PointerEvent e
		 * @return Promise<void>
		 */
		protected async Handle_OnClickConfirmNearby(e: PointerEvent): Promise<void> {
			e.preventDefault();

			this.selectedVenueModel = this.selectedVenueModel || this.nearbyVenueModel;
			this.slideshowView.next();
		}

		/**
		 * @param PointerEvent e
		 * @return Promise<void>
		 */
		protected async Handle_OnClickSearch(e: PointerEvent): Promise<void> {
			e.preventDefault();

			this.useNearbySuggestion = false;
		}

		/**
		 * @return Promise<void>
		 */
		protected async Handle_OnHide(): Promise<void> {
			this.close();
			this.reset();
		}

		/**
		 * Handle marker click from the nearby venue map. Swaps the suggested
		 * venue to the tapped one so its preview shows.
		 *
		 * @param ChalkySticks.Model.Venue venueModel
		 * @return Promise<void>
		 */
		protected async Handle_OnMarkerClick(venueModel: ChalkySticks.Model.Venue): Promise<void> {
			this.nearbyVenueOverride = venueModel;
		}

		/**
		 * Handle open event from the bus. Payload may carry a venueModel to
		 * pre-seed and skip the venue selection step.
		 *
		 * @param ChalkySticks.Core.IDispatcherEvent<{ venueModel?: ChalkySticks.Model.Venue }> e
		 * @return Promise<void>
		 */
		protected async Handle_OnOpen(
			e?: ChalkySticks.Core.IDispatcherEvent<{ venueModel?: ChalkySticks.Model.Venue }>,
		): Promise<void> {
			this.open(e?.data?.venueModel);
		}

		/**
		 * Handle venue selection from search.
		 *
		 * @param ChalkySticks.Model.Venue venueModel
		 * @return Promise<void>
		 */
		protected async Handle_OnSelectVenue(venueModel: ChalkySticks.Model.Venue): Promise<void> {
			this.selectedVenueModel = venueModel;
		}

		/**
		 * @param number index
		 * @return Promise<void>
		 */
		protected async Handle_OnSlideChange(index: number): Promise<void> {
			this.currentStep = index;
		}

		/**
		 * Handle successful table detection
		 *
		 * @param number probability
		 * @param any camera
		 * @param any detector
		 * @return Promise<void>
		 */
		protected async Handle_OnTableDetected(probability: number, camera: any, detector: any): Promise<void> {
			ChalkySticks.Utility.Haptic.medium();

			// this.$emit('table:detected', probability, camera, detector);
			await this.checkin();

			this.slideshowView.next();
		}

		/**
		 * Handle table detection progress
		 *
		 * @param number progress
		 * @return Promise<void>
		 */
		protected async Handle_OnTableProgress(progress: number): Promise<void> {
			// this.$emit('table:progress', progress);
		}

		// endregion: Event Handlers
	}
</script>

<style lang="scss">
	.view-modal-global-checkin {
		// Camera
		.slideshow-item:nth-child(2) {
			padding: 0;
		}

		.detector-container {
			height: 100%;
			overflow: hidden;
			position: relative;
		}

		.utility-dots,
		.chalky.venue-row .venue-status,
		.chalky.venue-cardpreview .actions {
			display: none;
		}

		.chalky.venue-map {
			border-radius: var(--border-radius);
			overflow: hidden;
		}

		// Nearby venue map + overlaid preview card
		.nearby-venue-container {
			height: 100%;
			position: relative;
			width: 100%;

			.chalky.venue-map {
				height: 100%;
				width: 100%;
			}

			.chalky.venue-cardpreview {
				bottom: 1rem;
				cursor: pointer;
				left: 1rem;
				position: absolute;
				right: 1rem;
				z-index: var(--z-page-mid);
			}
		}

		.venue-display {
			bottom: 1rem;
			left: 1rem;
			position: absolute;
			right: 1rem;

			.venue-row {
				backdrop-filter: blur(4px);
				background-color: rgba(255, 255, 255, 0.1);
			}
		}
	}
</style>
